/**
 * RTK Query — Base API
 *
 * Central `createApi` instance that all endpoint definitions extend via
 * `injectEndpoints`. Only ONE `createApi` call should exist per app;
 * additional endpoints are added by calling `baseApi.injectEndpoints()`
 * in their own files.
 *
 * ## Auth
 *
 * `prepareHeaders` reads the session token and tenant from Redux state and
 * attaches them as headers. An endpoint can opt out of either header via
 * `extraOptions.skipAuth` (no Authorization) or `extraOptions.skipTenant`
 * (no X-Tenant-Id). Public auth flows (accept-invitation, reset-*) set both;
 * the multipart upload step against a signed URL sets `skipAuth`.
 *
 * ## 401 wait+retry
 *
 * `baseQueryWithReauth` uses a single in-flight mutex so concurrent 401s do
 * not trigger multiple refreshes. On 401 it pulls a fresh JWT from Cognito,
 * updates the session in the store, and replays the original request. If the
 * refresh itself fails it dispatches `CLEAR_SESSION` to log the user out and
 * the caller surfaces the original error.
 *
 * ## 402 QUOTA_EXCEEDED
 *
 * Matches the legacy `apiMiddleware` behaviour: when the backend returns 402
 * with `{ error: { code: 'QUOTA_EXCEEDED', ... } }`, dispatch
 * `quotaExceeded(payload)` so the existing upgrade dialog opens.
 *
 * ## 403 ACCESS_DENIED
 *
 * Dispatch `ACCESS_DENIED` so listeners (route guards, 403 page) can react.
 */

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import config from '../../config';
import * as CognitoAuthService from '../CognitoAuthService';
import { setSession, clearSession } from '../../session/actions';
import { quotaExceeded } from '../../redux/features/quotaActions';

// Tiny mutex — one refresh at a time. Concurrent 401s queue on the same
// promise so they all see the refreshed token on retry.
let refreshPromise = null;

async function refreshAccessToken(api) {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    try {
      const cognitoSession = await CognitoAuthService.getCurrentSession();
      if (!cognitoSession) return null;
      const token = cognitoSession.getAccessToken().getJwtToken();
      const refreshToken = cognitoSession.getRefreshToken().getToken();
      const current = api.getState().session?.current || {};
      const newSession = { ...current, token, refreshToken };
      api.dispatch(setSession(newSession));
      return newSession;
    } catch (err) {
      return null;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

const rawBaseQuery = fetchBaseQuery({
  baseUrl: config.apiBaseUrl,
  prepareHeaders: (headers, { getState, extraOptions }) => {
    const opts = extraOptions || {};
    const session = getState().session?.current;
    if (session?.token && !opts.skipAuth) {
      headers.set('Authorization', `Bearer ${session.token}`);
    }
    if (session?.tenant && !opts.skipTenant) {
      headers.set('X-Tenant-Id', session.tenant);
    }
    return headers;
  },
});

const baseQueryWithReauth = async (args, api, extraOptions = {}) => {
  // Wait for any in-flight refresh before issuing the first attempt.
  if (refreshPromise) await refreshPromise;

  let result = await rawBaseQuery(args, api, extraOptions);

  if (result.error?.status === 401 && !extraOptions.skipAuth) {
    const refreshed = await refreshAccessToken(api);
    if (refreshed) {
      result = await rawBaseQuery(args, api, extraOptions);
    } else {
      api.dispatch(clearSession());
    }
  }

  if (result.error?.status === 402) {
    const payload = result.error.data?.error;
    if (payload && payload.code === 'QUOTA_EXCEEDED') {
      api.dispatch(quotaExceeded(payload));
    }
  }

  if (result.error?.status === 403) {
    api.dispatch({
      type: 'ACCESS_DENIED',
      payload: { url: typeof window !== 'undefined' ? window.location.pathname : '' },
    });
  }

  return result;
};

export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'AgentKey',
    'Tag',
    'EntityTags',
    'Resource',
    'User',
    'Item',
    'Flow',
    'Control',
    'Inspection',
    'InspectionItem',
    'PermissionAdmin',
    'PermissionUser',
    'Feature',
    'FlowType',
    'Autocomplete',
    'Search',
    'DashboardCard',
    'DashboardChart',
    'RecentControl',
    'CalendarEvent',
    'CalendarController',
    'AuthMe',
    'Workspaces',
    'Insight',
  ],
  endpoints: () => ({}),
});
